import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../../components/navigation/AdminHeader';
import AdminNavigation from '../../components/navigation/AdminNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { supabase } from '../../lib/supabase';
import InventoryGrid from './components/InventoryGrid';
import UsageLogModal from './components/UsageLogModal';
import ProcurementModal from './components/ProcurementModal';
import PartDetailPanel from './components/PartDetailPanel';

const PartsInventoryTracker = () => {
  const navigate = useNavigate();
  const [parts, setParts] = useState([]);
  const [filteredParts, setFilteredParts] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [showProcurementModal, setShowProcurementModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: 'all',
    status: 'all',
    location: 'all'
  });
  const [stats, setStats] = useState({
    totalParts: 0,
    lowStock: 0,
    outOfStock: 0,
    totalValue: 0
  });

  useEffect(() => {
    fetchParts();

    // Set up real-time subscription
    const subscription = supabase
      ?.channel('parts_inventory_changes')
      ?.on('postgres_changes', 
        { event: '*', schema: 'public', table: 'parts_inventory' },
        (payload) => {
          console.log('Real-time update:', payload);
          fetchParts();
        }
      )
      ?.subscribe();

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    applyFilters();
  }, [parts, searchTerm, filters]);

  useEffect(() => {
    calculateStats();
  }, [filteredParts]);

  const fetchParts = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        ?.from('parts_inventory')
        ?.select('*')
        ?.order('part_name', { ascending: true });

      if (fetchError) {
        console.error('Fetch error:', fetchError);
        setError('Failed to load parts inventory.');
        return;
      }

      setParts(data || []);
    } catch (err) {
      console.error('Error:', err);
      setError('An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...parts];

    // Search filter
    if (searchTerm?.trim()) {
      const query = searchTerm?.toLowerCase();
      filtered = filtered?.filter(part =>
        part?.part_name?.toLowerCase()?.includes(query) ||
        part?.part_number?.toLowerCase()?.includes(query) ||
        part?.description?.toLowerCase()?.includes(query) ||
        part?.barcode?.toLowerCase()?.includes(query)
      );
    }

    // Category filter
    if (filters?.category !== 'all') {
      filtered = filtered?.filter(part => part?.category === filters?.category);
    }

    // Status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(part => part?.status === filters?.status);
    }

    // Location filter
    if (filters?.location !== 'all') {
      filtered = filtered?.filter(part => part?.location === filters?.location);
    }

    setFilteredParts(filtered);
  };

  const calculateStats = () => {
    const totalParts = filteredParts?.length || 0;
    const lowStock = filteredParts?.filter(p => p?.status === 'low_stock')?.length || 0;
    const outOfStock = filteredParts?.filter(p => p?.status === 'out_of_stock')?.length || 0;
    const totalValue = filteredParts?.reduce((sum, part) => {
      const cost = parseFloat(part?.unit_cost || 0);
      const qty = parseInt(part?.quantity || 0);
      return sum + (cost * qty);
    }, 0);

    setStats({ totalParts, lowStock, outOfStock, totalValue });
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({
      category: 'all',
      status: 'all',
      location: 'all'
    });
  };

  const handlePartClick = (part) => {
    setSelectedPart(part);
    setShowDetailPanel(true);
  };

  const handleLogUsage = (part) => {
    setSelectedPart(part);
    setShowUsageModal(true);
  };

  const handleRequestProcurement = (part) => {
    setSelectedPart(part);
    setShowProcurementModal(true);
  };

  const handleUsageSubmit = async (usageData) => {
    try {
      // Insert usage log
      const { error: logError } = await supabase
        ?.from('parts_usage_log')
        ?.insert([usageData]);

      if (logError) throw logError;

      // Update part quantity
      const newQuantity = selectedPart?.quantity - usageData?.quantity_used;
      const { error: updateError } = await supabase
        ?.from('parts_inventory')
        ?.update({ quantity: newQuantity })
        ?.eq('id', selectedPart?.id);

      if (updateError) throw updateError;

      setShowUsageModal(false);
      fetchParts();
      alert('Usage logged successfully!');
    } catch (err) {
      console.error('Error logging usage:', err);
      alert('Failed to log usage: ' + err?.message);
    }
  };

  const handleProcurementSubmit = async (procurementData) => {
    try {
      const { error } = await supabase
        ?.from('procurement_requests')
        ?.insert([procurementData]);

      if (error) throw error;

      setShowProcurementModal(false);
      alert('Procurement request submitted successfully!');
    } catch (err) {
      console.error('Error submitting procurement request:', err);
      alert('Failed to submit request: ' + err?.message);
    }
  };

  const uniqueLocations = [...new Set(parts?.map(p => p?.location)?.filter(Boolean))];

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'Engine', label: 'Engine' },
    { value: 'Transmission', label: 'Transmission' },
    { value: 'Brakes', label: 'Brakes' },
    { value: 'Electrical', label: 'Electrical' },
    { value: 'Tires', label: 'Tires' },
    { value: 'Fluids', label: 'Fluids' },
    { value: 'Filters', label: 'Filters' },
    { value: 'Body', label: 'Body' },
    { value: 'Other', label: 'Other' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'in_stock', label: 'In Stock' },
    { value: 'low_stock', label: 'Low Stock' },
    { value: 'out_of_stock', label: 'Out of Stock' },
    { value: 'on_order', label: 'On Order' }
  ];

  const locationOptions = [
    { value: 'all', label: 'All Locations' },
    ...uniqueLocations?.map(loc => ({ value: loc, label: loc }))
  ];

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader />
      <AdminNavigation />

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Parts Inventory Tracker</h1>
            <p className="text-muted-foreground mt-1">Manage field inventory and track parts usage</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Parts</p>
                <p className="text-2xl font-bold text-foreground">{stats?.totalParts}</p>
              </div>
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="Package" size={24} className="text-primary" />
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Low Stock</p>
                <p className="text-2xl font-bold text-warning">{stats?.lowStock}</p>
              </div>
              <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center">
                <Icon name="AlertTriangle" size={24} className="text-warning" />
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Out of Stock</p>
                <p className="text-2xl font-bold text-error">{stats?.outOfStock}</p>
              </div>
              <div className="w-12 h-12 bg-error/10 rounded-full flex items-center justify-center">
                <Icon name="XCircle" size={24} className="text-error" />
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Value</p>
                <p className="text-2xl font-bold text-success">${stats?.totalValue?.toFixed(2)}</p>
              </div>
              <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center">
                <Icon name="DollarSign" size={24} className="text-success" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-card border border-border rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-1">
              <Input
                placeholder="Search parts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full"
              />
            </div>
            <Select
              options={categoryOptions}
              value={filters?.category}
              onChange={(value) => handleFilterChange('category', value)}
              placeholder="Category"
            />
            <Select
              options={statusOptions}
              value={filters?.status}
              onChange={(value) => handleFilterChange('status', value)}
              placeholder="Status"
            />
            <Select
              options={locationOptions}
              value={filters?.location}
              onChange={(value) => handleFilterChange('location', value)}
              placeholder="Location"
            />
          </div>
          {(searchTerm || filters?.category !== 'all' || filters?.status !== 'all' || filters?.location !== 'all') && (
            <div className="mt-4">
              <Button variant="outline" size="sm" onClick={handleClearFilters}>
                <Icon name="X" size={16} className="mr-2" />
                Clear Filters
              </Button>
            </div>
          )}
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-lg mb-6">
            <p>{error}</p>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Icon name="Loader2" size={48} className="animate-spin text-primary" />
          </div>
        )}

        {/* Inventory Grid */}
        {!isLoading && !error && (
          <InventoryGrid
            parts={filteredParts}
            onPartClick={handlePartClick}
            onLogUsage={handleLogUsage}
            onRequestProcurement={handleRequestProcurement}
          />
        )}

        {/* Empty State */}
        {!isLoading && !error && filteredParts?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Package" size={64} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No parts found</h3>
            <p className="text-muted-foreground">Try adjusting your search or filters</p>
          </div>
        )}
      </main>

      {/* Detail Panel */}
      {showDetailPanel && selectedPart && (
        <PartDetailPanel
          part={selectedPart}
          onClose={() => setShowDetailPanel(false)}
          onLogUsage={() => {
            setShowDetailPanel(false);
            setShowUsageModal(true);
          }}
          onRequestProcurement={() => {
            setShowDetailPanel(false);
            setShowProcurementModal(true);
          }}
        />
      )}

      {/* Usage Log Modal */}
      {showUsageModal && selectedPart && (
        <UsageLogModal
          part={selectedPart}
          onClose={() => setShowUsageModal(false)}
          onSubmit={handleUsageSubmit}
        />
      )}

      {/* Procurement Modal */}
      {showProcurementModal && selectedPart && (
        <ProcurementModal
          part={selectedPart}
          onClose={() => setShowProcurementModal(false)}
          onSubmit={handleProcurementSubmit}
        />
      )}
    </div>
  );
};

export default PartsInventoryTracker;
